/**
 * API Keys Page
 *
 * Manage API keys for programmatic access to the GPU Platform.
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Key,
  Plus,
  Copy,
  Trash2,
  AlertTriangle,
  Check,
  Clock,
  Shield,
  ExternalLink,
  Eye,
  EyeOff,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiKeysApi, ApiKey, NewApiKey, ApiKeyScope } from '../lib/api';
import clsx from 'clsx';

const AVAILABLE_SCOPES: { value: ApiKeyScope; label: string; description: string }[] = [
  { value: 'jobs:read', label: 'Read Jobs', description: 'List and view GPU jobs' },
  { value: 'jobs:write', label: 'Create Jobs', description: 'Create new GPU jobs' },
  { value: 'jobs:delete', label: 'Delete Jobs', description: 'Cancel/delete GPU jobs' },
  { value: 'usage:read', label: 'Read Usage', description: 'View usage records' },
  { value: 'keys:read', label: 'Read Keys', description: 'List API keys' },
  { value: 'keys:write', label: 'Manage Keys', description: 'Create/revoke API keys' },
];

export default function ApiKeysPage() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<NewApiKey | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  // Form state
  const [keyName, setKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<ApiKeyScope[]>(['jobs:read', 'jobs:write']);
  const [rateLimit, setRateLimit] = useState(60);
  const [expiresIn, setExpiresIn] = useState<string>('never');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      const keys = await apiKeysApi.list();
      setApiKeys(keys);
    } catch (error) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!keyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }
    if (selectedScopes.length === 0) {
      toast.error('Please select at least one scope');
      return;
    }

    setCreating(true);
    try {
      let expiresAt: string | undefined;
      if (expiresIn !== 'never') {
        const days = parseInt(expiresIn, 10);
        const date = new Date();
        date.setDate(date.getDate() + days);
        expiresAt = date.toISOString();
      }

      const newKey = await apiKeysApi.create({
        name: keyName,
        scopes: selectedScopes,
        rateLimit,
        expiresAt,
      });

      setNewlyCreatedKey(newKey);
      setShowCreateModal(false);
      await loadApiKeys();
      toast.success('API key created successfully');

      // Reset form
      setKeyName('');
      setSelectedScopes(['jobs:read', 'jobs:write']);
      setRateLimit(60);
      setExpiresIn('never');
    } catch (error) {
      toast.error('Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeKey = async (keyId: string, keyName: string) => {
    if (!confirm(`Are you sure you want to revoke "${keyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiKeysApi.revoke(keyId);
      await loadApiKeys();
      toast.success('API key revoked');
    } catch (error) {
      toast.error('Failed to revoke API key');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(true);
    toast.success('API key copied to clipboard');
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const toggleScope = (scope: ApiKeyScope) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold gradient-text">API Keys</h1>
          <p className="text-sm sm:text-base text-surface-400 mt-1">
            Manage API keys for programmatic access to the GPU Platform
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gpu-600 hover:bg-gpu-500
                     text-white font-medium rounded-lg transition-colors self-start sm:self-auto"
        >
          <Plus className="w-5 h-5" />
          Create API Key
        </button>
      </div>

      {/* Newly Created Key Alert */}
      <AnimatePresence>
        {newlyCreatedKey && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-amber-400">
                  Save Your API Key Now!
                </h3>
                <p className="text-surface-300 mt-1">
                  This is the only time you'll see this key. Copy it and store it securely.
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <code className="flex-1 bg-surface-900 px-4 py-3 rounded-lg font-mono text-sm text-surface-200 overflow-x-auto">
                    {showKey ? newlyCreatedKey.key : '•'.repeat(60)}
                  </code>
                  <button
                    onClick={() => setShowKey(!showKey)}
                    className="p-2 bg-surface-800 hover:bg-surface-700 rounded-lg transition-colors"
                    title={showKey ? 'Hide key' : 'Show key'}
                  >
                    {showKey ? (
                      <EyeOff className="w-5 h-5 text-surface-400" />
                    ) : (
                      <Eye className="w-5 h-5 text-surface-400" />
                    )}
                  </button>
                  <button
                    onClick={() => copyToClipboard(newlyCreatedKey.key)}
                    className={clsx(
                      'p-2 rounded-lg transition-colors',
                      copiedKey
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-surface-800 hover:bg-surface-700 text-surface-400'
                    )}
                    title="Copy to clipboard"
                  >
                    {copiedKey ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
                <button
                  onClick={() => {
                    setNewlyCreatedKey(null);
                    setShowKey(false);
                  }}
                  className="mt-4 text-sm text-surface-400 hover:text-surface-300"
                >
                  I've saved my key, dismiss this message
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Usage Instructions */}
      <div className="bg-surface-900/50 border border-surface-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-surface-200 mb-3">How to Use API Keys</h3>
        <div className="space-y-3 text-sm">
          <p className="text-surface-400">
            Use your API key to authenticate programmatic requests to the GPU Platform:
          </p>
          <div className="bg-surface-950 rounded-lg p-4 font-mono text-xs">
            <div className="text-surface-500"># Using Authorization header</div>
            <div className="text-surface-300">
              curl -H "Authorization: Bearer sk_live_your_key_here" \
            </div>
            <div className="text-surface-300 ml-4">http://localhost:3001/api/v1/gpu-jobs</div>
            <div className="text-surface-500 mt-3"># Or using X-API-Key header</div>
            <div className="text-surface-300">
              curl -H "X-API-Key: sk_live_your_key_here" \
            </div>
            <div className="text-surface-300 ml-4">http://localhost:3001/api/v1/gpu-jobs</div>
          </div>
          <a
            href="/api/docs"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-gpu-400 hover:text-gpu-300"
          >
            View API Documentation <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* API Keys List */}
      <div className="bg-surface-900/50 border border-surface-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-surface-800">
          <h2 className="text-lg font-semibold text-surface-200">Your API Keys</h2>
        </div>

        {loading ? (
          <div className="p-12 text-center text-surface-400">Loading...</div>
        ) : apiKeys.length === 0 ? (
          <div className="p-12 text-center">
            <Key className="w-12 h-12 text-surface-600 mx-auto mb-4" />
            <p className="text-surface-400">No API keys yet</p>
            <p className="text-surface-500 text-sm mt-1">
              Create an API key to start using the GPU Platform programmatically
            </p>
          </div>
        ) : (
          <div className="divide-y divide-surface-800">
            {apiKeys.map((key) => (
              <div key={key.id} className="p-6 hover:bg-surface-800/30 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div
                      className={clsx(
                        'w-10 h-10 rounded-lg flex items-center justify-center',
                        key.isActive ? 'bg-gpu-600/20' : 'bg-surface-700'
                      )}
                    >
                      <Key
                        className={clsx('w-5 h-5', key.isActive ? 'text-gpu-400' : 'text-surface-500')}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-surface-200">{key.name}</h3>
                        {!key.isActive && (
                          <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded">
                            Revoked
                          </span>
                        )}
                      </div>
                      <code className="text-sm text-surface-400 font-mono">{key.keyPrefix}...</code>
                      <div className="flex items-center gap-4 mt-2 text-xs text-surface-500">
                        <span className="flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          {key.scopes.length} scope{key.scopes.length !== 1 ? 's' : ''}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {key.lastUsedAt ? `Last used ${formatDate(key.lastUsedAt)}` : 'Never used'}
                        </span>
                        {key.expiresAt && (
                          <span>Expires {formatDate(key.expiresAt)}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {key.scopes.map((scope) => (
                          <span
                            key={scope}
                            className="px-2 py-0.5 text-xs bg-surface-800 text-surface-400 rounded"
                          >
                            {scope}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {key.isActive && (
                    <button
                      onClick={() => handleRevokeKey(key.id, key.name)}
                      className="p-2 text-surface-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Revoke API key"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create API Key Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-surface-900 border border-surface-700 rounded-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-surface-800">
                <h2 className="text-xl font-semibold text-surface-200">Create API Key</h2>
              </div>

              <div className="p-6 space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-2">
                    Key Name
                  </label>
                  <input
                    type="text"
                    value={keyName}
                    onChange={(e) => setKeyName(e.target.value)}
                    placeholder="e.g., Production Key, My AI Project"
                    className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg
                               text-surface-200 placeholder-surface-500
                               focus:outline-none focus:ring-2 focus:ring-gpu-500/50 focus:border-gpu-500"
                  />
                </div>

                {/* Scopes */}
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-2">
                    Permissions (Scopes)
                  </label>
                  <div className="space-y-2">
                    {AVAILABLE_SCOPES.map((scope) => (
                      <label
                        key={scope.value}
                        className={clsx(
                          'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                          selectedScopes.includes(scope.value)
                            ? 'bg-gpu-600/10 border-gpu-500/30'
                            : 'bg-surface-800/50 border-surface-700 hover:border-surface-600'
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={selectedScopes.includes(scope.value)}
                          onChange={() => toggleScope(scope.value)}
                          className="sr-only"
                        />
                        <div
                          className={clsx(
                            'w-5 h-5 rounded border flex items-center justify-center',
                            selectedScopes.includes(scope.value)
                              ? 'bg-gpu-600 border-gpu-500'
                              : 'border-surface-600'
                          )}
                        >
                          {selectedScopes.includes(scope.value) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-surface-200">{scope.label}</div>
                          <div className="text-xs text-surface-400">{scope.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Rate Limit */}
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-2">
                    Rate Limit (requests per minute)
                  </label>
                  <input
                    type="number"
                    value={rateLimit}
                    onChange={(e) => setRateLimit(parseInt(e.target.value, 10) || 60)}
                    min={1}
                    max={1000}
                    className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg
                               text-surface-200 focus:outline-none focus:ring-2 focus:ring-gpu-500/50 focus:border-gpu-500"
                  />
                </div>

                {/* Expiration */}
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-2">
                    Expiration
                  </label>
                  <select
                    value={expiresIn}
                    onChange={(e) => setExpiresIn(e.target.value)}
                    className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg
                               text-surface-200 focus:outline-none focus:ring-2 focus:ring-gpu-500/50 focus:border-gpu-500"
                  >
                    <option value="never">Never expires</option>
                    <option value="7">7 days</option>
                    <option value="30">30 days</option>
                    <option value="90">90 days</option>
                    <option value="365">1 year</option>
                  </select>
                </div>
              </div>

              <div className="p-6 border-t border-surface-800 flex justify-end gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-surface-400 hover:text-surface-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateKey}
                  disabled={creating}
                  className="flex items-center gap-2 px-4 py-2 bg-gpu-600 hover:bg-gpu-500
                             text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create API Key'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
